import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  advanceFixedStep,
  createFixedStepAccumulator,
  resetAccumulator,
} from './timing'

interface UseRafSimulationOptions {
  step: (dt: number) => void
  render?: (alpha: number) => void
  autoStart?: boolean
  enabled?: boolean
  fixedStep?: number
  maxFrameDt?: number
  maxStepsPerFrame?: number
  pauseWhenHidden?: boolean
}

interface UseRafSimulationApi {
  running: boolean
  start: () => void
  stop: () => void
  toggle: () => void
  stepOnce: () => void
}

function useLatest<T>(value: T) {
  const ref = useRef(value)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref
}

export function useRafSimulation(
  options: UseRafSimulationOptions,
): UseRafSimulationApi {
  const {
    autoStart = true,
    enabled = true,
    fixedStep = 1 / 60,
    maxFrameDt = 0.05,
    maxStepsPerFrame = 8,
    pauseWhenHidden = true,
  } = options

  const [running, setRunning] = useState(autoStart)
  const frameRef = useRef<number | null>(null)
  const lastTimestampRef = useRef<number | null>(null)

  const accumulatorRef = useRef(
    createFixedStepAccumulator({
      step: fixedStep,
      maxFrame: maxFrameDt,
      maxSteps: maxStepsPerFrame,
    }),
  )

  const stepRef = useLatest(options.step)
  const renderRef = useLatest(options.render)

  const visibilityHidden = () =>
    typeof document !== 'undefined' && document.hidden

  const stepOnce = useCallback(() => {
    stepRef.current(accumulatorRef.current.step)
    renderRef.current?.(0)
  }, [stepRef, renderRef])

  const stop = useCallback(() => {
    setRunning(false)
  }, [])

  const start = useCallback(() => {
    if (enabled) setRunning(true)
  }, [enabled])

  const toggle = useCallback(() => {
    setRunning((value) => (enabled ? !value : false))
  }, [enabled])

  useEffect(() => {
    const accumulator = accumulatorRef.current
    accumulator.step = fixedStep
    accumulator.maxFrame = maxFrameDt
    accumulator.maxSteps = maxStepsPerFrame
  }, [fixedStep, maxFrameDt, maxStepsPerFrame])

  useEffect(() => {
    if (!enabled) {
      setRunning(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled || !running) return

    const tick = (timestamp: number) => {
      if (pauseWhenHidden && visibilityHidden()) {
        lastTimestampRef.current = timestamp
        frameRef.current = requestAnimationFrame(tick)
        return
      }

      const last = lastTimestampRef.current
      lastTimestampRef.current = timestamp

      if (last !== null) {
        const frameDt = (timestamp - last) / 1000
        const update = advanceFixedStep(accumulatorRef.current, frameDt)

        for (let i = 0; i < update.steps; i++) {
          stepRef.current(update.stepDt)
        }

        renderRef.current?.(update.alpha)
      }

      frameRef.current = requestAnimationFrame(tick)
    }

    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
      lastTimestampRef.current = null
      resetAccumulator(accumulatorRef.current)
    }
  }, [enabled, pauseWhenHidden, running, renderRef, stepRef])

  return useMemo(
    () => ({
      running,
      start,
      stop,
      toggle,
      stepOnce,
    }),
    [running, start, stop, toggle, stepOnce],
  )
}
